import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, BedDouble, Car, Check, CheckCircle2, Compass, Download, HelpCircle, Landmark, Loader2, PencilLine } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { ConsentCheckboxes } from '../components/ConsentCheckboxes';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { apiFetch, downloadAuthedFile, formatEur, statusBadge } from '../utils/operations';

const inputCls = 'eco-input';
const CHANGE_OPTIONS = ['dates', 'accommodation', 'experiences', 'transfers', 'travellers', 'other'];

const Section = ({ title, icon: Icon, children, testid }) => (
  <section data-testid={testid} className="rounded-2xl border border-eco-ink/10 bg-white p-6">
    <h2 className="flex items-center gap-2 text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">{Icon && <Icon size={17} className="text-eco-gold" />} {title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

export default function ProposalPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const locale = { pt: 'pt-PT', en: 'en-GB', de: 'de-DE', fr: 'fr-FR' }[lang] || 'pt-PT';
  const statusLabel = (s) => { const label = t(`pst.${s}`); return label === `pst.${s}` ? s : label; };
  useSEO('Proposta | Eco Collection', 'A sua proposta de viagem Eco Collection.');
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [changesOpen, setChangesOpen] = useState(false);
  const [consents, setConsents] = useState({ terms: false, cancellation: false, marketing: false });
  const [questionOpen, setQuestionOpen] = useState(false);
  const [changeTypes, setChangeTypes] = useState([]);
  const [note, setNote] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(`/traveller/proposals/${id}`);
      setProposal(data);
    } catch (err) {
      setProposal(null);
      setError(err.status === 401 ? 'authentication' : t('prop.notfound'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const respond = async (action) => {
    setBusy(action);
    try {
      await apiFetch(`/traveller/proposals/${id}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, change_types: changeTypes, note, consent_terms: consents.terms, consent_cancellation: consents.cancellation }),
      });
      toast.success(t(`prop.${action}.ok`));
      setChangesOpen(false); setQuestionOpen(false); setNote(''); setChangeTypes([]);
      await load();
    } catch (err) {
      toast.error(t('prop.error'), { description: String(err.message) });
    } finally {
      setBusy('');
    }
  };

  if (!proposal) {
    return (
      <div data-testid="proposal-gate">
        <PageHeader kicker="Eco Collection" title={t('prop.gate.title')} sub={t('prop.gate.sub')} />
        <section className="bg-eco-cream py-14">
          <div className="mx-auto max-w-md px-5">
            <div data-testid="proposal-gate-form" className="rounded-2xl border border-eco-ink/10 bg-white p-6 text-center shadow-sm">
              {loading ? <Loader2 className="mx-auto animate-spin" /> : <><p data-testid="proposal-gate-error" className="text-sm text-eco-ink/65">{error === 'authentication' ? t('traveller.access.required') : error}</p><Link to="/my-trip" data-testid="proposal-gate-submit" className="eco-btn-primary mt-4 w-full">{t('traveller.access.cta')}</Link></>}
            </div>
          </div>
        </section>
      </div>
    );
  }

  const p = proposal;
  const actionable = ['sent', 'changes_requested'].includes(p.status);
  const acc = p.accommodation || {};
  const cond = p.conditions || {};

  return (
    <div data-testid="proposal-page">
      <PageHeader kicker={`${p.proposal_ref || p.reference} · V${p.version}`} title={t('prop.title')} sub={`${p.destination}${p.travel_dates ? ` · ${p.travel_dates}` : ''}${p.travellers ? ` · ${p.travellers}` : ''}`} />
      <section className="bg-eco-cream py-10 md:py-14">
        <div className="eco-container max-w-4xl space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link to="/my-trip" data-testid="proposal-back-link" className="eco-btn-text"><ArrowLeft size={15} /> {t('back')}</Link>
            <div className="flex items-center gap-3">
              <span data-testid="proposal-status" className={`rounded-full px-3.5 py-1.5 text-xs font-bold ${statusBadge(p.status)}`}>{statusLabel(p.status)}</span>
              <button type="button" onClick={() => downloadAuthedFile(`/traveller/proposals/${p.id}/pdf`, `${p.proposal_ref || 'proposal'}.pdf`).catch(() => toast.error(t('prop.error')))} data-testid="proposal-pdf-download" className="eco-btn-secondary !min-h-10 px-4 text-sm"><Download size={15} /> PDF</button>
            </div>
          </div>

          {p.message && <p className="rounded-xl bg-eco-gold/10 px-4 py-3 text-sm text-eco-ink/70" data-testid="proposal-message">{p.message}</p>}

          {p.itinerary?.length > 0 && (
            <Reveal><Section title={t('prop.journey')} icon={Compass} testid="proposal-itinerary">
              <ol className="space-y-3">
                {p.itinerary.map((d, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="grid h-8 w-14 shrink-0 place-items-center rounded-lg bg-eco-green text-xs font-bold text-eco-gold">{d.day}</span>
                    <div><p className="text-sm font-semibold text-eco-ink">{d.title}</p>{d.description && <p className="text-sm text-eco-ink/60">{d.description}</p>}</div>
                  </li>
                ))}
              </ol>
            </Section></Reveal>
          )}

          {Object.values(acc).some(Boolean) && (
            <Reveal><Section title={t('prop.accommodation')} icon={BedDouble} testid="proposal-accommodation">
              <dl className="grid gap-3 sm:grid-cols-2">
                {[['prop.acc.property', acc.property_name], ['prop.acc.room', acc.room_type], ['prop.acc.nights', acc.nights], ['prop.acc.meal', acc.meal_basis]].map(([k, v]) => v && (
                  <div key={k}><dt className="eco-field-label">{t(k)}</dt><dd className="mt-0.5 text-sm font-medium text-eco-ink">{v}</dd></div>
                ))}
              </dl>
            </Section></Reveal>
          )}

          {p.experiences?.length > 0 && (
            <Reveal><Section title={t('prop.experiences')} icon={Compass} testid="proposal-experiences">
              <ul className="space-y-2">
                {p.experiences.map((e, i) => <li key={i} className="text-sm text-eco-ink/75"><strong>{e.name}</strong>{e.date && ` · ${e.date}`}{e.duration && ` · ${e.duration}`}{e.partner && ` · ${e.partner}`}</li>)}
              </ul>
            </Section></Reveal>
          )}

          {p.transfers?.length > 0 && (
            <Reveal><Section title={t('prop.transfers')} icon={Car} testid="proposal-transfers">
              <ul className="space-y-2">
                {p.transfers.map((tr, i) => <li key={i} className="text-sm text-eco-ink/75"><strong>{tr.route}</strong>{tr.vehicle && ` · ${tr.vehicle}`}{tr.pickup && ` · ${tr.pickup}`}</li>)}
              </ul>
            </Section></Reveal>
          )}

          {(p.included?.length > 0 || p.not_included?.length > 0) && (
            <Reveal><Section title={t('prop.included')} icon={Check} testid="proposal-included">
              <div className="grid gap-4 sm:grid-cols-2">
                <ul className="space-y-1.5">{p.included?.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm text-eco-ink/75"><Check size={14} className="mt-0.5 shrink-0 text-eco-green" /> {s}</li>)}</ul>
                {p.not_included?.length > 0 && <ul className="space-y-1.5">{p.not_included.map((s, i) => <li key={i} className="text-sm text-eco-ink/50">— {s}</li>)}</ul>}
              </div>
            </Section></Reveal>
          )}

          <Reveal>
            <section data-testid="proposal-total" className="rounded-2xl border border-eco-green/15 bg-white p-6">
              <h2 className="text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">Total</h2>
              <p data-testid="proposal-price" className="eco-number mt-2 text-3xl font-bold tracking-[-0.02em] text-eco-green">{formatEur(p.price_public_eur)}</p>
              {p.customer_currency !== 'EUR' && p.customer_amount && <p className="eco-number mt-1 text-sm font-semibold text-eco-ink/60">{p.customer_amount.toFixed(2)} {p.customer_currency} · {t('prop.fxrates')} {p.rates_snapshot?.captured_at ? new Date(p.rates_snapshot.captured_at).toLocaleDateString(locale) : ''}</p>}
              {p.deposit_amount_eur != null && (
                <div className="mt-3 grid gap-2 rounded-xl bg-eco-cream p-4 text-sm sm:grid-cols-3">
                  <p>{t('prop.deposit')} ({p.deposit_pct}%): <strong className="eco-number">{formatEur(p.deposit_amount_eur)}</strong></p>
                  <p>{t('prop.remaining')}: <strong className="eco-number">{formatEur(p.remaining_balance_eur)}</strong></p>
                  {p.payment_deadline && <p>{t('prop.due')}: <strong>{p.payment_deadline}</strong></p>}
                </div>
              )}
            </section>
          </Reveal>

          <Reveal><Section title={t('prop.conditions')} icon={CheckCircle2} testid="proposal-conditions">
            <dl className="space-y-3">
              {[['prop.cond.validity', p.valid_until || cond.validity], ['prop.cond.cancellation', cond.cancellation], ['prop.cond.modification', cond.modification], ['prop.cond.important', cond.important_info]].map(([k, v]) => v && (
                <div key={k}><dt className="eco-field-label">{t(k)}</dt><dd className="mt-0.5 text-sm text-eco-ink/75">{v}</dd></div>
              ))}
            </dl>
          </Section></Reveal>

          {p.accepted_snapshot && (
            <p data-testid="proposal-accepted-banner" className="rounded-xl bg-eco-green/10 px-4 py-3 text-sm font-semibold text-eco-green">
              {t('prop.accepted')} · V{p.accepted_snapshot.version} · {new Date(p.accepted_snapshot.accepted_at).toLocaleString(locale)}
            </p>
          )}

          {p.accepted_snapshot && p.payment_instructions && (
            <Reveal>
              <section data-testid="payment-instructions" className="rounded-2xl border border-eco-gold/30 bg-white p-6 md:p-7">
                <h3 className="flex items-center gap-2 text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">
                  <Landmark size={18} className="text-eco-gold" /> {t('pay.bank.t')}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-eco-ink/65">{t('pay.bank.s')}</p>
                <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl bg-eco-cream px-4 py-3">
                  <span className="eco-field-label">{t('pay.ref')}</span>
                  <span data-testid="payment-reference" className="eco-number text-base font-bold text-eco-ink">{p.payment_instructions.reference}</span>
                  <span data-testid="payment-amount-due" className="eco-number ml-auto text-base font-bold text-eco-green">{p.payment_instructions.amount_eur != null ? formatEur(p.payment_instructions.amount_eur) : ''}</span>
                </div>
                {p.payment_instructions.accounts.length > 0 ? (
                  <div className="mt-4 space-y-3">
                    {p.payment_instructions.accounts.map((a) => (
                      <div key={a.name} data-testid={`payment-account-${a.name.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-xl border border-eco-ink/10 p-4">
                        <p className="text-sm font-semibold text-eco-ink">{a.name} <span className="ml-1 rounded-full bg-eco-ink/5 px-2 py-0.5 text-[10px] font-bold text-eco-ink/50">{a.currency}</span></p>
                        <p className="eco-number mt-1.5 text-sm tracking-wide text-eco-ink/80"><span className="eco-field-label mr-2">IBAN</span>{a.iban}</p>
                        {a.details && <p className="mt-1 text-xs text-eco-ink/55">{a.details}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p data-testid="payment-instructions-pending" className="mt-4 rounded-xl bg-eco-cream px-4 py-3 text-sm text-eco-ink/60">{t('pay.bank.pending')}</p>
                )}
              </section>
            </Reveal>
          )}
          {p.status === 'changes_requested' && p.change_request && (
            <p data-testid="proposal-changes-banner" className="rounded-xl bg-eco-gold/15 px-4 py-3 text-sm font-medium text-eco-ink/75">
              {t('prop.changessent')}{p.change_request.note ? `: ${p.change_request.note}` : ''}
            </p>
          )}

          {actionable && (
            <p className="text-center text-xs text-eco-ink/50" data-testid="proposal-support-note">{t('support.sub')}</p>
          )}

          {actionable && (
            <div data-testid="proposal-actions" className="space-y-4 rounded-2xl border border-eco-ink/10 bg-white p-6">
              <ConsentCheckboxes testid="proposal-consent" value={consents} onChange={setConsents} showCancellation />
              <div className="flex flex-wrap gap-3">
                <button data-testid="proposal-accept" onClick={() => respond('accept')} disabled={!!busy || !consents.terms} className="eco-btn-primary flex-1 justify-center disabled:opacity-60">{busy === 'accept' ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} {t('prop.accept')}</button>
                <button data-testid="proposal-request-changes" onClick={() => { setChangesOpen(!changesOpen); setQuestionOpen(false); }} className="eco-btn-secondary flex-1 justify-center"><PencilLine size={15} /> {t('prop.changes')}</button>
                <button data-testid="proposal-ask-question" onClick={() => { setQuestionOpen(!questionOpen); setChangesOpen(false); }} className="eco-btn-secondary flex-1 justify-center"><HelpCircle size={15} /> {t('prop.question')}</button>
              </div>
              {changesOpen && (
                <div data-testid="proposal-changes-form" className="space-y-3 border-t border-eco-ink/10 pt-4">
                  <div className="flex flex-wrap gap-2">
                    {CHANGE_OPTIONS.map((o) => (
                      <button type="button" key={o} data-testid={`proposal-change-${o}`} onClick={() => setChangeTypes((ct) => ct.includes(o) ? ct.filter((x) => x !== o) : [...ct, o])} className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${changeTypes.includes(o) ? 'bg-eco-green text-white' : 'bg-eco-cream text-eco-ink/60 hover:bg-eco-green/10'}`}>{t(`prop.change.${o}`)}</button>
                    ))}
                  </div>
                  <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('prop.changenote')} className={inputCls} data-testid="proposal-changes-note" />
                  <button data-testid="proposal-changes-submit" onClick={() => respond('request_changes')} disabled={busy === 'request_changes' || (changeTypes.length === 0 && !note.trim())} className="eco-btn-primary disabled:opacity-60">{t('prop.changessubmit')}</button>
                </div>
              )}
              {questionOpen && (
                <div data-testid="proposal-question-form" className="space-y-3 border-t border-eco-ink/10 pt-4">
                  <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('prop.questionnote')} className={inputCls} data-testid="proposal-question-note" />
                  <button data-testid="proposal-question-submit" onClick={() => respond('question')} disabled={busy === 'question' || !note.trim()} className="eco-btn-primary disabled:opacity-60">{t('prop.questionsubmit')}</button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
